import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { ModalComponent } from '@ui/modal/modal.component';
import { InputComponent } from '@ui/input/input.component';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MunicipalitiesService } from '@api/services/municipalities.service';
import { TendersService } from '@api/services/tenders.service';
import { PowerBiSettingsService } from '@api/services/powerbi-settings.service';
import { Municipality } from '@api/models/municipality.model';
import { CommonModule } from '@angular/common';
import { SelectComponent } from '@ui/select/select.component';
import { switchMap } from 'rxjs';

@Component({
  selector: 'app-municipalities-modal',
  standalone: true,
  imports: [
    ModalComponent,
    InputComponent,
    SelectComponent,
    ReactiveFormsModule,
    CommonModule,
  ],
  template: `
    <ui-modal
      [title]="municipality?.id ? 'Gemeente bewerken' : 'Nieuwe gemeente'"
      [submitText]="'Opslaan'"
      [cancelText]="'Annuleren'"
      (close)="closeModal()"
      (save)="saveModal()"
    >
      <form [formGroup]="form">
        <div class="flex flex-col gap-4">
          <ui-input id="code" label="Code" placeholder="Code" formControlName="code" />
          <ui-input id="name" label="Naam" placeholder="Naam" formControlName="name" />
          <ui-input
            id="customer_number"
            label="AFAS CustomerNumber"
            placeholder="Debiteurnummer gemeente"
            formControlName="customer_number"
          />
          <div class="grid grid-cols-2 gap-4">
            <ui-input
              type="checkbox"
              id="csv_invoicing"
              label="CSV facturering"
              formControlName="csv_invoicing"
            />
            <ui-input
              *ngIf="form.get('csv_invoicing')?.value"
              id="cbs_code"
              label="CBS code"
              placeholder="CBS code"
              formControlName="cbs_code"
              [required]="true"
            />
          </div>

          <div *ngIf="form.get('csv_invoicing')?.value" class="grid grid-cols-2 gap-4">
            <ui-select
              id="tender_id"
              label="Aanbesteding"
              placeholder="Selecteer een aanbesteding"
              [options]="tenderOptions"
              formControlName="tender_id"
              [required]="true"
            />

            <ui-input
              id="finance_email"
              label="Finance e-mailadres"
              placeholder="Finance e-mailadres"
              formControlName="finance_email"
              [required]="true"
            />
          </div>

          @if (municipality?.id) {
            <div
              class="rounded-lg border border-slate-200 p-4 flex flex-col gap-3"
              formGroupName="powerbi"
            >
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-medium text-slate-900">Power BI (RLS)</h3>
                @if (powerBiConfigured()) {
                  <span
                    class="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded"
                  >
                    Geconfigureerd
                  </span>
                } @else {
                  <span
                    class="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded"
                  >
                    Niet geconfigureerd
                  </span>
                }
              </div>
              <p class="text-xs text-slate-500">
                De rolnaam moet exact overeenkomen met de rol in Power BI
                Desktop (hoofdlettergevoelig).
              </p>
              <ui-input
                id="rls_role"
                label="Power BI rol"
                placeholder="bijv. Connect Groningen"
                formControlName="rls_role"
                [required]="true"
              />
            </div>
          }
        </div>
      </form>
    </ui-modal>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MunicipalitiesModalComponent implements OnInit {
  private modalRef = inject(MODAL_REF);
  private cdr = inject(ChangeDetectorRef);
  modalService = inject(ModalService);
  municipalitiesService = inject(MunicipalitiesService);
  tendersService = inject(TendersService);
  powerBiSettingsService = inject(PowerBiSettingsService);

  municipality: Municipality | null = null;
  form: FormGroup = new FormGroup({});
  tenderOptions: { label: string; value: string }[] = [];
  powerBiConfigured = signal(false);

  ngOnInit() {
    this.tendersService.getTenders(1, 100).subscribe((response) => {
      this.tenderOptions = response.data.map((tender) => ({
        label: tender.name,
        value: tender.id!,
      }));
      this.cdr.markForCheck();
    });

    this.form = new FormGroup({
      name: new FormControl(this.municipality?.name, Validators.required),
      code: new FormControl(this.municipality?.code, Validators.required),
      customer_number: new FormControl(this.municipality?.customer_number ?? ''),
      active: new FormControl(this.municipality?.active ?? true, Validators.required),
      csv_invoicing: new FormControl(this.municipality?.csv_invoicing ?? false),
      tender_id: new FormControl(this.municipality?.tender?.id),
      cbs_code: new FormControl(this.municipality?.cbs_code),
      finance_email: new FormControl(this.municipality?.finance_email),
      powerbi: new FormGroup({
        rls_role: new FormControl('', [
          Validators.required,
          Validators.maxLength(255),
        ]),
      }),
    });

    if (!this.municipality?.id) {
      this.form.removeControl('powerbi');
    } else {
      this.loadPowerBiSettings(this.municipality.id);
    }

    this.form.get('csv_invoicing')?.valueChanges.subscribe((csvInvoicing) => {
      const tenderIdControl = this.form.get('tender_id');
      const cbsCodeControl = this.form.get('cbs_code');
      const financeEmailControl = this.form.get('finance_email');

      if (csvInvoicing) {
        tenderIdControl?.setValidators([Validators.required]);
        cbsCodeControl?.setValidators([Validators.required]);
        financeEmailControl?.setValidators([
          Validators.required,
          Validators.email,
        ]);
      } else {
        tenderIdControl?.clearValidators();
        tenderIdControl?.setValue(null);
        cbsCodeControl?.clearValidators();
        cbsCodeControl?.setValue('');
        financeEmailControl?.clearValidators();
        financeEmailControl?.setValue('');
      }

      tenderIdControl?.updateValueAndValidity();
      cbsCodeControl?.updateValueAndValidity();
      financeEmailControl?.updateValueAndValidity();
      this.cdr.markForCheck();
    });
  }

  private loadPowerBiSettings(municipalityId: string): void {
    this.powerBiSettingsService.getSettings(municipalityId).subscribe({
      next: (settings) => {
        this.powerBiConfigured.set(settings?.is_configured ?? false);
        this.form.get('powerbi')?.patchValue({
          rls_role: settings?.rls_role ?? '',
        });
        this.cdr.markForCheck();
      },
      error: (error) => console.error('Error loading Power BI settings:', error),
    });
  }

  closeModal() {
    if (this.modalRef) {
      this.modalService.close(this.modalRef as ModalRef<unknown>);
    }
  }

  saveModal() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const { powerbi, ...municipalityData } = this.form.value;

    if (this.municipality?.id) {
      this.municipalitiesService
        .updateMunicipality(
          this.municipality.id,
          municipalityData as Municipality
        )
        .pipe(
          switchMap(() =>
            this.powerBiSettingsService.updateSettings(
              this.municipality!.id!,
              powerbi.rls_role
            )
          )
        )
        .subscribe({
          next: (settings) => {
            this.powerBiConfigured.set(settings.is_configured);
            this.closeModal();
          },
          error: (error) =>
            console.error('Error saving municipality or Power BI settings:', error),
        });
    } else {
      this.municipalitiesService
        .createMunicipality(municipalityData as Municipality)
        .subscribe({
          next: () => this.closeModal(),
          error: (error) =>
            console.error('Error creating municipality:', error),
        });
    }
  }
}
